/**
 * Wave 25 — 10 new cities
 * Lille 🇫🇷, Nuremberg 🇩🇪, Szczecin 🇵🇱, Constanța 🇷🇴, Tartu 🇪🇪,
 * Indore 🇮🇳, Nagoya Hub2 🇯🇵, Asunción 🇵🇾, Lomé 🇹🇬, Jeddah Hub2 🇸🇦
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling. React, Node.js, Python, cloud-native.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools. Modern stack with React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter. From MVP to enterprise-grade mobile solutions.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, recommendation engines, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring, and 24/7 managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists, and project managers integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems. Figma-first approach with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance (GDPR, HIPAA, Enterprise Security Standards).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools, and banking applications with enterprise-grade security.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management, and omnichannel retail solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine platforms, EHR integrations, and HIPAA-compliant health applications.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies from seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management, and supply chain visibility platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE25: Record<string, CityAgencyData> = {

  lille: {
    slug: 'software-development-agency-lille',
    cityName: 'Lille',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Lille | Call IT Dev',
      fr: 'Agence de Développement Informatique Lille | Call IT Dev',
      es: 'Agencia Desarrollo Software Lille | Call IT Dev',
      de: 'Softwareentwicklung Agentur Lille | Call IT Dev',
      ar: 'وكالة تطوير برمجيات ليل | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Lille & Hauts-de-France. SaaS, web, mobile, AI. Native French engineers, CET timezone, from €25/hr. 60% savings vs local.',
      fr: 'Agence de développement informatique pour Lille et les Hauts-de-France. SaaS, web, mobile, IA. Ingénieurs francophones natifs, dès 25 €/h. 60% d\'économies.',
      es: 'Agencia de desarrollo de software para Lille. SaaS, web, móvil, IA. Ingenieros francófonos, desde 25 €/h. 60% ahorro.',
      de: 'Softwareentwicklungsagentur für Lille. SaaS, Web, Mobile, KI. Französischsprachige Ingenieure, ab 25 €/h.',
      ar: 'وكالة تطوير برمجيات لمدينة ليل. SaaS، ويب، موبايل، ذكاء اصطناعي. مهندسون فرنكوفونيون، من 25 €/ساعة.',
    },
    keywords: ['software development agency Lille', 'agence développement informatique Lille', 'nearshore Hauts-de-France', 'IT outsourcing Lille'],
    heroTitle: {
      en: 'Software Development Agency for Lille',
      fr: 'Agence de Développement Informatique pour Lille',
      es: 'Agencia de Desarrollo de Software para Lille',
      de: 'Softwareentwicklung Agentur für Lille',
      ar: 'وكالة تطوير برمجيات لمدينة ليل',
    },
    heroSubtitle: {
      en: 'Native French-speaking engineers at nearshore rates. Same CET timezone, 60% savings vs Lille agencies. Scale your Hauts-de-France tech team.',
      fr: 'Ingénieurs francophones natifs à tarifs nearshore. Même fuseau CET, 60% d\'économies vs agences lilloises. Renforcez votre équipe tech.',
      es: 'Ingenieros francófonos a precios nearshore. Misma zona CET, 60% ahorro vs agencias locales.',
      de: 'Französischsprachige Ingenieure zu Nearshore-Preisen. Gleiche CET-Zeitzone, 60% Ersparnis.',
      ar: 'مهندسون فرنكوفونيون بأسعار nearshore. نفس المنطقة الزمنية CET، توفير 60%.',
    },
    whyTitle: {
      en: 'Why Lille Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Lilloises Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Lille Eligen Call IT Dev',
      de: 'Warum Lilleer Unternehmen Call IT Dev Wählen',
      ar: 'لماذا تختار شركات ليل Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Native French Engineers', description: 'All developers speak fluent French. Seamless communication for Hauts-de-France businesses.' },
      { icon: '⏰', title: 'CET Timezone (Identical)', description: 'Work 9-to-6 same as Lille. Real-time collaboration, no delays.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at €25-40/hr vs €70-100/hr in Lille. Same quality, RGPD compliant.' },
      { icon: '🏭', title: 'Industry 4.0 Expertise', description: 'Experience with logistics, retail, and manufacturing — key Lille industries.' },
      { icon: '✈️', title: 'Easy Access', description: 'Direct flights from Lille-Lesquin. 3.5h to Casablanca HQ.' },
      { icon: '🚀', title: 'Fast Ramp-Up', description: 'Dedicated team operational in 2 weeks. Skip the competitive Lille hiring market.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do your engineers speak native French?', answer: 'Yes, all engineers assigned to French clients are native French speakers (C2 minimum). Documentation, meetings, and code reviews in French.' },
      { question: 'How does nearshore compare to hiring in Lille?', answer: 'A senior developer in Lille costs €70-100/hr all-in. Our nearshore rate is €25-40/hr for equivalent expertise, with CET timezone alignment.' },
      { question: 'Can you work with Lille startups?', answer: 'Absolutely. We support seed to Series C startups. Flexible engagement from 2 devs to 20+.' },
      { question: 'Do you comply with RGPD?', answer: 'Yes. Enterprise-grade security, RGPD/GDPR compliant. DPA signed. All data handling follows CNIL guidelines.' },
      { question: 'How quickly can you start?', answer: 'Team onboarding takes 2 weeks. CVs within 5 business days. First sprint starts week 3.' },
      { question: 'What engagement models do you offer?', answer: 'Time & materials, fixed-price, and dedicated team models. Discovery phase of 1-2 weeks for fixed-price.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '60%+',
  },

  nuremberg: {
    slug: 'software-development-agency-nuremberg',
    cityName: 'Nuremberg',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Nuremberg | Call IT Dev',
      fr: 'Agence de Développement Informatique Nuremberg | Call IT Dev',
      de: 'Softwareentwicklung Agentur Nürnberg | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Nuremberg & Bavaria. SaaS, web, mobile, AI. German-speaking engineers, CET timezone, from €25/hr. 55% savings.',
      fr: 'Agence de développement informatique pour Nuremberg. SaaS, web, mobile, IA. Ingénieurs germanophones, dès 25 €/h.',
      de: 'Softwareentwicklungsagentur für Nürnberg und Bayern. SaaS, Web, Mobile, KI. Deutschsprachige Ingenieure, ab 25 €/h. 55% Ersparnis.',
    },
    keywords: ['software development agency Nuremberg', 'Softwareentwicklung Nürnberg', 'IT outsourcing Bavaria', 'nearshore development Germany'],
    heroTitle: {
      en: 'Software Development Agency for Nuremberg',
      fr: 'Agence de Développement Informatique pour Nuremberg',
      de: 'Softwareentwicklung Agentur für Nürnberg',
    },
    heroSubtitle: {
      en: 'German-speaking engineers at nearshore rates. CET timezone, 55% savings vs Bavarian agencies. Enterprise-grade software development.',
      fr: 'Ingénieurs germanophones à tarifs nearshore. Fuseau CET, 55% d\'économies vs agences bavaroises.',
      de: 'Deutschsprachige Ingenieure zu Nearshore-Preisen. CET-Zeitzone, 55% Ersparnis vs bayerische Agenturen.',
    },
    whyTitle: {
      en: 'Why Nuremberg Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Nuremberg Choisissent Call IT Dev',
      de: 'Warum Nürnberger Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'German-Speaking Engineers', description: 'Fluent German developers for seamless communication. B2/C1 minimum.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Identical timezone to Nuremberg. Real-time collaboration 9-to-6.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €25-40/hr vs €80-120/hr in Bavaria. Same quality, DSGVO compliant.' },
      { icon: '🏭', title: 'Industry 4.0 & Automotive', description: 'Deep experience with Bavarian automotive and manufacturing sectors.' },
      { icon: '🛡️', title: 'DSGVO Compliant', description: 'Enterprise-grade security, DSGVO/GDPR compliant. German data protection standards.' },
      { icon: '🚀', title: 'Fast Team Scaling', description: 'Dedicated team in 2 weeks. No local recruitment overhead.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do your engineers speak German?', answer: 'Yes, we provide German-speaking developers (B2-C1) for Bavarian clients. Meetings and documentation in German.' },
      { question: 'What is the cost comparison to Nuremberg?', answer: 'Senior developers in Nuremberg cost €80-120/hr. Our nearshore rate is €25-40/hr with the same CET timezone.' },
      { question: 'Do you comply with DSGVO?', answer: 'Yes. Enterprise-grade security, DSGVO/GDPR compliant. AVV signed. German data protection standards met.' },
      { question: 'Can you work with automotive companies?', answer: 'Yes. Extensive experience with IATF 16949, automotive software, and Industry 4.0 systems.' },
      { question: 'How quickly can you start?', answer: 'Team onboarding in 2 weeks. CVs within 5 business days.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure. Full-stack and specialized.' },
    ],
    localRate: '€80-120',
    ourRate: '€25-40',
    savings: '55%+',
  },

  szczecin: {
    slug: 'software-development-agency-szczecin',
    cityName: 'Szczecin',
    countryName: 'Poland',
    flag: '🇵🇱',
    timezone: 'CET (GMT+1)',
    currency: 'PLN/EUR',
    title: {
      en: 'Software Development Agency Szczecin | Call IT Dev',
      fr: 'Agence de Développement Informatique Szczecin | Call IT Dev',
      de: 'Softwareentwicklung Agentur Stettin | Call IT Dev',
      pl: 'Agencja Rozwoju Oprogramowania Szczecin | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Szczecin & Western Pomerania. SaaS, web, mobile, AI. CET timezone, from €20/hr. 50% savings vs Berlin.',
      fr: 'Agence de développement informatique pour Szczecin. SaaS, web, mobile, IA. Fuseau CET, dès 20 €/h.',
      de: 'Softwareentwicklungsagentur für Stettin. SaaS, Web, Mobile, KI. CET-Zeitzone, ab 20 €/h. 50% Ersparnis vs Berlin.',
      pl: 'Agencja rozwoju oprogramowania w Szczecinie. SaaS, web, mobile, AI. Strefa CET, od 20 €/h.',
    },
    keywords: ['software development agency Szczecin', 'IT outsourcing Szczecin', 'nearshore Poland', 'agencja IT Szczecin'],
    heroTitle: {
      en: 'Software Development Agency for Szczecin',
      fr: 'Agence de Développement Informatique pour Szczecin',
      de: 'Softwareentwicklung Agentur für Stettin',
      pl: 'Agencja Rozwoju Oprogramowania dla Szczecina',
    },
    heroSubtitle: {
      en: 'Expert engineers at competitive rates. CET timezone, 50% savings vs Berlin. Scale your Western Pomerania tech operations.',
      fr: 'Ingénieurs experts à tarifs compétitifs. Fuseau CET, 50% d\'économies vs Berlin.',
      de: 'Experten-Ingenieure zu wettbewerbsfähigen Preisen. CET-Zeitzone, 50% Ersparnis vs Berlin.',
      pl: 'Eksperci inżynierowie w konkurencyjnych cenach. Strefa CET, 50% oszczędności vs Berlin.',
    },
    whyTitle: {
      en: 'Why Szczecin Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Szczecin Choisissent Call IT Dev',
      de: 'Warum Stettiner Unternehmen Call IT Dev Wählen',
      pl: 'Dlaczego Firmy ze Szczecina Wybierają Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'Cross-Border Expertise', description: 'Bridge between Polish and German markets. Multilingual teams for Pomerania region.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone as Szczecin. Real-time collaboration without delays.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Senior devs at €20-35/hr. Significant savings vs Western European rates.' },
      { icon: '🎓', title: 'Strong Tech Talent', description: 'Access to Polish university graduates and experienced engineers.' },
      { icon: '🛡️', title: 'EU GDPR Compliant', description: 'Enterprise-grade security, GDPR compliant. Full EU data protection standards.' },
      { icon: '🚀', title: 'Rapid Scaling', description: 'Dedicated team in 2 weeks. Flexible from 2 to 30+ developers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have Polish-speaking developers?', answer: 'Yes, we provide Polish and English-speaking developers for Szczecin-based clients.' },
      { question: 'How close are you to Szczecin timezone?', answer: 'We operate in CET — identical to Szczecin. Full workday overlap.' },
      { question: 'What is the pricing?', answer: 'Senior developers from €20-35/hr. 50% savings vs Berlin and Western European rates.' },
      { question: 'Do you comply with EU GDPR?', answer: 'Yes. Enterprise-grade security, GDPR compliant. DPA signed for all engagements.' },
      { question: 'Can you handle enterprise projects?', answer: 'Absolutely. We support startups to enterprises with scalable teams.' },
      { question: 'How fast can you start?', answer: 'Dedicated team in 2 weeks. CVs within 5 business days.' },
    ],
    localRate: '€50-80',
    ourRate: '€20-35',
    savings: '50%+',
  },

  constanta: {
    slug: 'software-development-agency-constanta',
    cityName: 'Constanța',
    countryName: 'Romania',
    flag: '🇷🇴',
    timezone: 'EET (GMT+2)',
    currency: 'RON/EUR',
    title: {
      en: 'Software Development Agency Constanța | Call IT Dev',
      fr: 'Agence de Développement Informatique Constanța | Call IT Dev',
      de: 'Softwareentwicklung Agentur Constanța | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Constanța & Romanian Black Sea coast. SaaS, web, mobile, AI. EET timezone, from €20/hr. 55% savings.',
      fr: 'Agence de développement informatique pour Constanța. SaaS, web, mobile, IA. Fuseau EET, dès 20 €/h. 55% d\'économies.',
      de: 'Softwareentwicklungsagentur für Constanța. SaaS, Web, Mobile, KI. EET-Zeitzone, ab 20 €/h. 55% Ersparnis.',
    },
    keywords: ['software development agency Constanta', 'IT outsourcing Romania', 'nearshore Constanta', 'web development Romania'],
    heroTitle: {
      en: 'Software Development Agency for Constanța',
      fr: 'Agence de Développement Informatique pour Constanța',
      de: 'Softwareentwicklung Agentur für Constanța',
    },
    heroSubtitle: {
      en: 'Romanian engineering talent at nearshore rates. EET timezone, 55% savings vs Western Europe. Black Sea tech hub.',
      fr: 'Talents d\'ingénierie roumains à tarifs nearshore. Fuseau EET, 55% d\'économies vs Europe de l\'Ouest.',
      de: 'Rumänische Ingenieurtalente zu Nearshore-Preisen. EET-Zeitzone, 55% Ersparnis vs Westeuropa.',
    },
    whyTitle: {
      en: 'Why Constanța Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Constanța Choisissent Call IT Dev',
      de: 'Warum Unternehmen aus Constanța Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🌊', title: 'Black Sea Tech Hub', description: 'Growing tech ecosystem on Romania\'s coast. Maritime and logistics expertise.' },
      { icon: '⏰', title: 'EET Timezone', description: 'Only 1 hour ahead of CET. Full overlap with European clients.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €20-35/hr vs €70-110/hr in Western Europe.' },
      { icon: '🎓', title: 'Romanian Tech Talent', description: 'Romania produces 9,000+ IT graduates annually. Strong STEM tradition.' },
      { icon: '🇪🇺', title: 'EU Member State', description: 'Full EU GDPR compliance. EU data residency available.' },
      { icon: '🚀', title: 'Fast Scaling', description: 'Dedicated team in 2 weeks. Flexible engagement models.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Constanța for software development?', answer: 'Constanța offers growing tech talent, EU membership, EET timezone, and 55% savings vs Western Europe.' },
      { question: 'Do you have English-speaking developers?', answer: 'Yes, all engineers speak fluent English. Romanian developers have high English proficiency.' },
      { question: 'What is the cost comparison?', answer: 'Senior developers from €20-35/hr vs €70-110/hr in Western Europe. 55%+ savings.' },
      { question: 'Do you comply with EU regulations?', answer: 'Yes. Enterprise-grade security, GDPR compliant. Full EU data protection standards.' },
      { question: 'What industries do you serve?', answer: 'FinTech, e-commerce, healthcare, SaaS, logistics, and maritime industries.' },
      { question: 'How fast can you start?', answer: 'Dedicated team in 2 weeks. CVs within 5 business days.' },
    ],
    localRate: '€70-110',
    ourRate: '€20-35',
    savings: '55%+',
  },

  tartu: {
    slug: 'software-development-agency-tartu',
    cityName: 'Tartu',
    countryName: 'Estonia',
    flag: '🇪🇪',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Tartu | Call IT Dev',
      fr: 'Agence de Développement Informatique Tartu | Call IT Dev',
      de: 'Softwareentwicklung Agentur Tartu | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Tartu & Southern Estonia. SaaS, web, mobile, AI. EET timezone, from €25/hr. Digital nation expertise.',
      fr: 'Agence de développement informatique pour Tartu. SaaS, web, mobile, IA. Fuseau EET, dès 25 €/h. Expertise nation numérique.',
      de: 'Softwareentwicklungsagentur für Tartu. SaaS, Web, Mobile, KI. EET-Zeitzone, ab 25 €/h.',
    },
    keywords: ['software development agency Tartu', 'IT outsourcing Estonia', 'nearshore Tartu', 'e-Estonia development'],
    heroTitle: {
      en: 'Software Development Agency for Tartu',
      fr: 'Agence de Développement Informatique pour Tartu',
      de: 'Softwareentwicklung Agentur für Tartu',
    },
    heroSubtitle: {
      en: 'Estonian digital-first engineers at competitive rates. EET timezone, e-Estonia expertise. University of Tartu tech ecosystem.',
      fr: 'Ingénieurs estoniens digital-first à tarifs compétitifs. Fuseau EET, expertise e-Estonie.',
      de: 'Estnische Digital-First-Ingenieure zu wettbewerbsfähigen Preisen. EET-Zeitzone, e-Estonia-Expertise.',
    },
    whyTitle: {
      en: 'Why Tartu Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Tartu Choisissent Call IT Dev',
      de: 'Warum Tartuer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🎓', title: 'University of Tartu Ecosystem', description: 'Access to Estonia\'s top computer science talent. Strong research-to-industry pipeline.' },
      { icon: '⏰', title: 'EET Timezone', description: 'Only 1 hour ahead of CET. Full workday overlap with EU clients.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Competitive rates vs Tallinn and Nordic agencies. High quality, lower cost.' },
      { icon: '🇪🇪', title: 'e-Estonia Digital Nation', description: 'Built in the world\'s most digital country. Deep e-governance and fintech expertise.' },
      { icon: '🇪🇺', title: 'EU Data Compliance', description: 'Enterprise-grade security, GDPR, eIDAS compliant. EU data residency guaranteed.' },
      { icon: '🚀', title: 'Startup-Friendly', description: 'Experience with Tartu\'s vibrant startup scene. Flexible MVP-to-scale engagements.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Tartu for software development?', answer: 'Tartu offers University of Tartu talent, e-Estonia digital expertise, EU compliance, and competitive pricing.' },
      { question: 'Do engineers speak English?', answer: 'Yes, Estonian developers have excellent English proficiency. All communication in English.' },
      { question: 'What is the pricing?', answer: 'Senior developers from €25-40/hr. 50% savings vs Nordic and Western European agencies.' },
      { question: 'Do you work with Estonian startups?', answer: 'Yes. Extensive experience with the Tartu and Estonian startup ecosystem.' },
      { question: 'EU GDPR compliance?', answer: 'Yes. Enterprise-grade security, GDPR, eIDAS compliant. Full EU data protection.' },
      { question: 'How fast to start?', answer: 'Dedicated team in 2 weeks. First sprint week 3.' },
    ],
    localRate: '€60-90',
    ourRate: '€25-40',
    savings: '50%+',
  },

  indore: {
    slug: 'software-development-agency-indore',
    cityName: 'Indore',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Indore | Call IT Dev',
      fr: 'Agence de Développement Informatique Indore | Call IT Dev',
      de: 'Softwareentwicklung Agentur Indore | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Indore & Madhya Pradesh. SaaS, web, mobile, AI. IST timezone, from $15/hr. 75% savings vs US/EU.',
      fr: 'Agence de développement informatique pour Indore. SaaS, web, mobile, IA. Fuseau IST, dès 15 $/h. 75% d\'économies.',
      de: 'Softwareentwicklungsagentur für Indore. SaaS, Web, Mobile, KI. IST-Zeitzone, ab 15 $/h. 75% Ersparnis.',
    },
    keywords: ['software development agency Indore', 'IT outsourcing Indore', 'offshore development India', 'web development Indore'],
    heroTitle: {
      en: 'Software Development Agency for Indore',
      fr: 'Agence de Développement Informatique pour Indore',
      de: 'Softwareentwicklung Agentur für Indore',
    },
    heroSubtitle: {
      en: 'India\'s Smart City engineers at offshore rates. IST timezone, 75% savings. Emerging tech hub of Central India.',
      fr: 'Ingénieurs de la Smart City indienne à tarifs offshore. Fuseau IST, 75% d\'économies.',
      de: 'Indiens Smart-City-Ingenieure zu Offshore-Preisen. IST-Zeitzone, 75% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Indore Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Indore Choisissent Call IT Dev',
      de: 'Warum Indorer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🏙️', title: 'Smart City Hub', description: 'India\'s cleanest city and emerging IT hub. Growing startup ecosystem.' },
      { icon: '🎓', title: 'IIT & IIM Talent', description: 'Proximity to IIT Indore and IIM Indore. Top-tier engineering graduates.' },
      { icon: '💰', title: '75% Cost Savings', description: 'Senior devs at $15-30/hr vs $80-150/hr in US/EU. Enterprise quality.' },
      { icon: '🌐', title: 'English Fluency', description: 'All engineers fluent in English. Seamless international communication.' },
      { icon: '🔧', title: 'Full-Stack Expertise', description: 'React, Node.js, Python, Java, AWS, Azure. Specialized AI/ML teams.' },
      { icon: '🚀', title: 'Scale Rapidly', description: 'From 2 to 50+ developers. Flexible engagement models.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Indore for development?', answer: 'Indore offers IIT/IIM talent, Smart City infrastructure, competitive rates, and 75% savings vs US/EU.' },
      { question: 'Do engineers speak English?', answer: 'Yes, all engineers are fluent in English. India is the world\'s largest English-speaking tech workforce.' },
      { question: 'What is the pricing?', answer: 'Senior developers from $15-30/hr. 75% savings vs US/EU rates.' },
      { question: 'What technologies?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, Docker, Kubernetes.' },
      { question: 'Data security?', answer: 'Enterprise-grade security, GDPR compliant. NDAs signed. Encrypted communications.' },
      { question: 'How fast to start?', answer: 'Dedicated team in 2 weeks. First sprint week 3.' },
    ],
    localRate: '$80-150',
    ourRate: '$15-30',
    savings: '75%+',
  },

  nagoya_hub2: {
    slug: 'software-development-agency-nagoya-hub2',
    cityName: 'Nagoya',
    countryName: 'Japan',
    flag: '🇯🇵',
    timezone: 'JST (GMT+9)',
    currency: 'JPY/USD',
    title: {
      en: 'Software Development Agency Nagoya — Automotive Tech | Call IT Dev',
      fr: 'Agence Développement Informatique Nagoya — Tech Automobile | Call IT Dev',
      ja: '名古屋ソフトウェア開発エージェンシー — 自動車テック | Call IT Dev',
    },
    description: {
      en: 'Software development agency for Nagoya\'s automotive and manufacturing sector. SaaS, IoT, AI, Industry 4.0. From $25/hr. 65% savings.',
      fr: 'Agence de développement pour le secteur automobile de Nagoya. SaaS, IoT, IA, Industrie 4.0. Dès 25 $/h. 65% d\'économies.',
      ja: '名古屋の自動車・製造業向けソフトウェア開発。SaaS、IoT、AI、インダストリー4.0。25$/時間〜。65%コスト削減。',
    },
    keywords: ['software development agency Nagoya', 'ソフトウェア開発 名古屋', 'automotive software Nagoya', 'IoT development Japan'],
    heroTitle: {
      en: 'Software Development Agency for Nagoya — Automotive Tech',
      fr: 'Agence de Développement pour Nagoya — Tech Automobile',
      ja: '名古屋向けソフトウェア開発 — 自動車テック',
    },
    heroSubtitle: {
      en: 'IoT, AI, and Industry 4.0 for Japan\'s automotive capital. 65% savings vs Tokyo agencies. Toyota-region expertise.',
      fr: 'IoT, IA et Industrie 4.0 pour la capitale automobile du Japon. 65% d\'économies vs Tokyo.',
      ja: '日本の自動車首都向けIoT、AI、インダストリー4.0。東京のエージェンシーより65%節約。',
    },
    whyTitle: {
      en: 'Why Nagoya Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Nagoya Choisissent Call IT Dev',
      ja: 'なぜ名古屋企業がCall IT Devを選ぶのか',
    },
    whyReasons: [
      { icon: '🚗', title: 'Automotive Software Expertise', description: 'Deep experience with automotive IoT, connected vehicles, and manufacturing systems.' },
      { icon: '🏭', title: 'Industry 4.0', description: 'Smart factory solutions, predictive maintenance, and production optimization.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $25-45/hr vs $80-150/hr in Nagoya/Tokyo.' },
      { icon: '🤖', title: 'AI & ML Excellence', description: 'Computer vision, NLP, predictive analytics for manufacturing.' },
      { icon: '🛡️', title: 'IATF 16949 Ready', description: 'Understanding of automotive quality standards and compliance.' },
      { icon: '🚀', title: 'Scale Flexibly', description: 'From 2 to 30+ engineers. Dedicated automotive-focused teams.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have automotive software experience?', answer: 'Yes. Extensive experience with IoT, connected vehicles, ADAS, and manufacturing systems for automotive OEMs.' },
      { question: 'Can engineers communicate in Japanese?', answer: 'We provide bilingual project managers (EN/JP) and technical documentation in Japanese.' },
      { question: 'What about Industry 4.0?', answer: 'Smart factory, predictive maintenance, digital twins, and production optimization platforms.' },
      { question: 'Cost comparison to Nagoya?', answer: 'Senior developers $25-45/hr vs $80-150/hr locally. 65% savings with automotive expertise.' },
      { question: 'IATF 16949 compliance?', answer: 'We understand automotive quality standards and can develop software in compliance with IATF 16949.' },
      { question: 'How fast to start?', answer: 'Dedicated team in 2-3 weeks. First sprint by week 4.' },
    ],
    localRate: '¥12,000-18,000',
    ourRate: '$25-45',
    savings: '65%+',
  },

  asuncion: {
    slug: 'software-development-agency-asuncion',
    cityName: 'Asunción',
    countryName: 'Paraguay',
    flag: '🇵🇾',
    timezone: 'PYT (GMT-4)',
    currency: 'PYG/USD',
    title: {
      en: 'Software Development Agency Asunción | Call IT Dev',
      fr: 'Agence de Développement Informatique Asunción | Call IT Dev',
      es: 'Agencia Desarrollo Software Asunción | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Asunción & Paraguay. SaaS, web, mobile, AI. US timezone overlap, from $15/hr. 75% savings vs US.',
      fr: 'Agence de développement informatique pour Asunción. SaaS, web, mobile, IA. Chevauchement US, dès 15 $/h. 75% d\'économies.',
      es: 'Agencia de desarrollo de software en Asunción, Paraguay. SaaS, web, móvil, IA. Zona horaria US, desde 15 $/h. 75% ahorro.',
    },
    keywords: ['software development agency Asuncion', 'IT outsourcing Paraguay', 'nearshore development Asuncion', 'desarrollo software Paraguay'],
    heroTitle: {
      en: 'Software Development Agency for Asunción',
      fr: 'Agence de Développement Informatique pour Asunción',
      es: 'Agencia de Desarrollo de Software para Asunción',
    },
    heroSubtitle: {
      en: 'Nearshore talent in Paraguay with US timezone overlap. 75% savings vs American agencies. Growing Latin American tech hub.',
      fr: 'Talents nearshore au Paraguay avec chevauchement US. 75% d\'économies vs agences américaines.',
      es: 'Talento nearshore en Paraguay con solapamiento horario con EEUU. 75% ahorro vs agencias americanas.',
    },
    whyTitle: {
      en: 'Why Asunción Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Asunción Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Asunción Eligen Call IT Dev',
    },
    whyReasons: [
      { icon: '🌎', title: 'US Timezone Overlap', description: 'GMT-4 timezone. 4-6 hours overlap with US East Coast for real-time collaboration.' },
      { icon: '💰', title: '75% Cost Savings', description: 'Senior devs at $15-30/hr vs $80-150/hr in the US.' },
      { icon: '🗣️', title: 'Bilingual Teams', description: 'Spanish/English bilingual developers. Guaraní-Spanish cultural bridge.' },
      { icon: '🇵🇾', title: 'Growing Tech Hub', description: 'Paraguay\'s tech ecosystem is rapidly expanding with government support.' },
      { icon: '🔧', title: 'Full-Stack Teams', description: 'React, Node.js, Python, mobile — complete development capabilities.' },
      { icon: '🚀', title: 'Fast Deployment', description: 'Dedicated team in 2 weeks. Agile methodology, weekly sprints.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Paraguay for software development?', answer: 'Paraguay offers competitive rates, US timezone overlap, bilingual talent, and a growing tech ecosystem.' },
      { question: 'Do engineers speak English?', answer: 'Yes, all engineers are bilingual Spanish/English. Meetings in either language.' },
      { question: 'Cost comparison to US?', answer: 'Senior developers from $15-30/hr vs $80-150/hr in the US. 75% savings.' },
      { question: 'What technologies?', answer: 'React, TypeScript, Node.js, Python, React Native, Flutter, AWS, Azure.' },
      { question: 'Data security?', answer: 'Enterprise Security Standards, GDPR compliant. NDAs signed. Encrypted communications.' },
      { question: 'How fast to start?', answer: 'Dedicated team in 2 weeks. First sprint week 3.' },
    ],
    localRate: '$80-150',
    ourRate: '$15-30',
    savings: '75%+',
  },

  lome: {
    slug: 'software-development-agency-lome',
    cityName: 'Lomé',
    countryName: 'Togo',
    flag: '🇹🇬',
    timezone: 'GMT (UTC+0)',
    currency: 'XOF/EUR',
    title: {
      en: 'Software Development Agency Lomé | Call IT Dev',
      fr: 'Agence de Développement Informatique Lomé | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Lomé & Togo. SaaS, web, mobile, AI. GMT timezone, from $12/hr. 80% savings. West Africa tech hub.',
      fr: 'Agence de développement informatique à Lomé, Togo. SaaS, web, mobile, IA. Fuseau GMT, dès 12 $/h. 80% d\'économies. Hub tech Afrique de l\'Ouest.',
    },
    keywords: ['software development agency Lome', 'agence développement informatique Lomé', 'IT outsourcing Togo', 'nearshore Afrique Ouest'],
    heroTitle: {
      en: 'Software Development Agency for Lomé',
      fr: 'Agence de Développement Informatique pour Lomé',
    },
    heroSubtitle: {
      en: 'West African tech talent at ultra-competitive rates. GMT timezone, 80% savings. Togo\'s emerging digital economy.',
      fr: 'Talents tech ouest-africains à tarifs ultra-compétitifs. Fuseau GMT, 80% d\'économies. Économie numérique émergente du Togo.',
    },
    whyTitle: {
      en: 'Why Lomé Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Lomé Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'West Africa Tech Hub', description: 'Lomé is emerging as a tech center. Government-backed digital transformation.' },
      { icon: '⏰', title: 'GMT Timezone', description: 'Same as London. Full overlap with UK and Western European clients.' },
      { icon: '💰', title: '80% Cost Savings', description: 'Senior devs from $12-25/hr vs $80-150/hr in EU/US.' },
      { icon: '🗣️', title: 'Francophone Talent', description: 'Native French-speaking developers. Bridge to West African markets.' },
      { icon: '🇹🇬', title: 'Digital Togo', description: 'Government-supported digital economy with fiber optic infrastructure.' },
      { icon: '🚀', title: 'Rapid Deployment', description: 'Dedicated team in 2 weeks. Agile development from day 1.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Lomé for software development?', answer: 'Lomé offers francophone talent, GMT timezone, ultra-competitive rates, and government-backed digital infrastructure.' },
      { question: 'Do engineers speak French?', answer: 'Yes, all engineers are native French speakers. English proficiency also available.' },
      { question: 'Cost comparison?', answer: 'Senior developers from $12-25/hr. 80% savings vs EU/US rates.' },
      { question: 'Internet infrastructure?', answer: 'Togo has invested heavily in fiber optic. Lomé has reliable high-speed connectivity.' },
      { question: 'Data security?', answer: 'Enterprise Security Standards, GDPR compliant.' },
      { question: 'How fast to start?', answer: 'Dedicated team in 2 weeks. First sprint week 3.' },
    ],
    localRate: '$80-150',
    ourRate: '$12-25',
    savings: '80%+',
  },

  jeddah_hub2: {
    slug: 'software-development-agency-jeddah-hub2',
    cityName: 'Jeddah',
    countryName: 'Saudi Arabia',
    flag: '🇸🇦',
    timezone: 'AST (GMT+3)',
    currency: 'SAR/USD',
    title: {
      en: 'Software Development Agency Jeddah — Vision 2030 | Call IT Dev',
      fr: 'Agence Développement Informatique Jeddah — Vision 2030 | Call IT Dev',
      ar: 'وكالة تطوير برمجيات جدة — رؤية 2030 | Call IT Dev',
    },
    description: {
      en: 'Software development agency for Jeddah\'s Vision 2030 transformation. SaaS, fintech, e-commerce, AI. Arabic/English teams, from $25/hr. 60% savings.',
      fr: 'Agence de développement pour la transformation Vision 2030 de Jeddah. SaaS, fintech, e-commerce, IA. Équipes arabe/anglais, dès 25 $/h.',
      ar: 'وكالة تطوير برمجيات لتحول رؤية 2030 في جدة. SaaS، فينتك، تجارة إلكترونية، ذكاء اصطناعي. فرق عربية/إنجليزية، من 25 $/ساعة.',
    },
    keywords: ['software development agency Jeddah', 'وكالة تطوير برمجيات جدة', 'Vision 2030 IT', 'app development Jeddah'],
    heroTitle: {
      en: 'Software Development Agency for Jeddah — Vision 2030',
      fr: 'Agence de Développement pour Jeddah — Vision 2030',
      ar: 'وكالة تطوير برمجيات لجدة — رؤية 2030',
    },
    heroSubtitle: {
      en: 'Arabic/English bilingual teams powering Jeddah\'s digital transformation. FinTech, e-commerce, smart city. 60% savings vs local.',
      fr: 'Équipes bilingues arabe/anglais pour la transformation numérique de Jeddah. FinTech, e-commerce, ville intelligente.',
      ar: 'فرق ثنائية اللغة عربية/إنجليزية تدعم التحول الرقمي في جدة. فينتك، تجارة إلكترونية، مدينة ذكية. توفير 60%.',
    },
    whyTitle: {
      en: 'Why Jeddah Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Jeddah Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات جدة Call IT Dev',
    },
    whyReasons: [
      { icon: '🏙️', title: 'Vision 2030 Expertise', description: 'Deep experience with Saudi digital transformation and NEOM-related projects.' },
      { icon: '🗣️', title: 'Arabic/English Teams', description: 'Native Arabic-speaking developers. Full RTL and Arabic UI expertise.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at $25-45/hr vs $80-150/hr locally. Same quality.' },
      { icon: '💳', title: 'FinTech & E-commerce', description: 'Mada, STC Pay, SADAD integration. Saudi e-commerce platform development.' },
      { icon: '🛡️', title: 'NCA Compliance Ready', description: 'Understanding of Saudi National Cybersecurity Authority requirements.' },
      { icon: '🚀', title: 'Rapid Scaling', description: 'Dedicated team in 2 weeks. Flexible from 2 to 30+ developers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have Arabic-speaking developers?', answer: 'Yes, native Arabic-speaking engineers. Full Arabic UI/UX and RTL support expertise.' },
      { question: 'Vision 2030 project experience?', answer: 'Extensive experience with Saudi digital transformation, e-governance, fintech, and smart city projects.' },
      { question: 'Cost comparison to Jeddah?', answer: 'Senior developers $25-45/hr vs $80-150/hr locally. 60% savings with bilingual teams.' },
      { question: 'NCA compliance?', answer: 'We understand Saudi NCA requirements and can develop compliant software solutions.' },
      { question: 'Saudi payment integrations?', answer: 'Mada, STC Pay, SADAD, Apple Pay KSA. Full Saudi payment gateway expertise.' },
      { question: 'How fast to start?', answer: 'Dedicated team in 2 weeks. First sprint by week 3.' },
    ],
    localRate: 'SAR 350-600',
    ourRate: '$25-45',
    savings: '60%+',
  },

};
